import { supabase } from 'lib/supabaseClient';
import { getList } from 'services/erpApi';

export type GlobalSearchItem = {
  id: string;
  entityType: string;
  code: string;
  title: string;
  subtitle?: string;
  route: string;
  score?: number;
};

const normalize = (value: string) => value.trim();

const buildWorkcenterSubtitle = (workcenter?: any) => {
  if (!workcenter) return '';
  return [workcenter.workcenter_code, workcenter.workcenter_name].filter(Boolean).join(' - ');
};

const buildCustomerSubtitle = (row: any) => {
  const line = row.customer_order_line;
  const customer = [line?.customer_sequence_no, line?.order_no, line?.customer_name].filter(Boolean).join(' / ');
  return [customer, row.status].filter(Boolean).join(' · ');
};

const buildItemTitle = (row: any) => {
  const item = [row.items?.item_code, row.items?.item_name].filter(Boolean).join(' ');
  return item || row.wo_no || '-';
};

const fallbackSearch = async (query: string, limit = 12): Promise<GlobalSearchItem[]> => {
  const [itemsResult, workOrdersResult, operationsResult, workcentersResult, customerLinesResult] = await Promise.all([
    getList('items', {
      filters: [{ field: ['item_code', 'item_name', 'category'], op: 'ilike', value: query }],
      sort: { field: 'item_code', direction: 'asc' },
      page: 1,
      pageSize: limit,
      select: 'id, item_code, item_name, category'
    }),
    getList('work_orders', {
      filters: [{ field: ['wo_no'], op: 'ilike', value: query }],
      sort: { field: 'created_at', direction: 'desc' },
      page: 1,
      pageSize: limit,
      select:
        'id, wo_no, status, items (item_code, item_name), customer_order_line:customer_order_lines (id, order_no, customer_sequence_no, customer_name)'
    }),
    getList('operations', {
      filters: [{ field: ['operation_code', 'operation_name', 'department'], op: 'ilike', value: query }],
      sort: { field: 'operation_code', direction: 'asc' },
      page: 1,
      pageSize: limit,
      select: `
        id,
        operation_code,
        operation_name,
        department,
        workcenters (
          id,
          workcenter_code,
          workcenter_name
        )
      `
    }),
    getList('workcenters', {
      filters: [{ field: ['workcenter_code', 'workcenter_name', 'department'], op: 'ilike', value: query }],
      sort: { field: 'workcenter_code', direction: 'asc' },
      page: 1,
      pageSize: limit,
      select: 'id, workcenter_code, workcenter_name, department'
    }),
    getList('customer_order_lines', {
      filters: [{ field: ['customer_name', 'order_no', 'customer_sequence_no'], op: 'ilike', value: query }],
      sort: { field: 'created_at', direction: 'desc' },
      page: 1,
      pageSize: limit,
      select: 'id'
    })
  ]);

  const customerLineIds = (customerLinesResult.data as any[]).map((row) => row.id).filter(Boolean);
  const customerWorkOrdersResult =
    customerLineIds.length > 0
      ? await getList('work_orders', {
          filters: [{ field: 'customer_order_line_id', op: 'in', value: customerLineIds }],
          sort: { field: 'created_at', direction: 'desc' },
          page: 1,
          pageSize: limit,
          select:
            'id, wo_no, status, items (item_code, item_name), customer_order_line:customer_order_lines (id, order_no, customer_sequence_no, customer_name)'
        })
      : { data: [] };

  const items = (itemsResult.data as any[]).map((row) => ({
    id: row.id,
    entityType: 'ITEM',
    code: row.item_code || '-',
    title: row.item_name || row.item_code || '-',
    subtitle: row.category || '',
    route: `/erp/items/${row.id}`,
    score: 0
  }));

  const workOrderMap = new Map<string, any>();
  [...(workOrdersResult.data as any[]), ...(customerWorkOrdersResult.data as any[])].forEach((row) => {
    if (row?.id) workOrderMap.set(row.id, row);
  });

  const workOrders = Array.from(workOrderMap.values()).map((row) => ({
    id: row.id,
    entityType: 'WORK_ORDER',
    code: row.wo_no || '-',
    title: buildItemTitle(row),
    subtitle: buildCustomerSubtitle(row),
    route: `/erp/work-orders/${row.id}/operations`,
    score: 0
  }));

  const operations = (operationsResult.data as any[]).map((row) => ({
    id: row.id,
    entityType: 'OPERATION',
    code: row.operation_code || '-',
    title: row.operation_name || row.operation_code || '-',
    subtitle: buildWorkcenterSubtitle(row.workcenters),
    route: '/erp/operations',
    score: 0
  }));

  const workcenters = (workcentersResult.data as any[]).map((row) => ({
    id: row.id,
    entityType: 'WORKCENTER',
    code: row.workcenter_code || '-',
    title: row.workcenter_name || row.workcenter_code || '-',
    subtitle: row.department || '',
    route: '/erp/workcenters',
    score: 0
  }));

  return [...items, ...workOrders, ...operations, ...workcenters].slice(0, limit);
};

export const searchGlobal = async (rawQuery: string, limit = 12): Promise<GlobalSearchItem[]> => {
  const query = normalize(rawQuery);
  if (!query) return [];

  try {
    const { data, error } = await supabase.rpc('erp_global_search', {
      p_query: query,
      p_limit: limit
    });

    if (error) throw error;

    return ((data || []) as any[]).map((row) => ({
      id: row.entity_id,
      entityType: row.entity_type,
      code: row.code || '-',
      title: row.title || row.code || '-',
      subtitle: row.subtitle || '',
      route: row.route || '/',
      score: row.score || 0
    }));
  } catch {
    return fallbackSearch(query, limit);
  }
};
